/** Edit product → tab «Характеристики» — заполнение EAV-значений (Phase B2).
 *
 * Раньше новый атрибут (или новое значение dropdown, например ещё один
 * цвет) можно было завести только на отдельной странице «Конфигурация →
 * Атрибуты», уходя с карточки товара. Добавлены inline-формы: «+ Новый
 * атрибут» (создаёт атрибут и сразу привязывает к текущему Product Type)
 * и «+ значение» у каждого dropdown/multiselect поля.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface ProductType {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  has_variants: boolean;
  attribute_ids: number[];
}

const NEW_ATTR_INPUT_TYPE_LABELS: Record<FilledValue["input_type"], string> = {
  dropdown: "Один из списка",
  multiselect: "Несколько из списка",
  text: "Текст",
  numeric: "Число",
  boolean: "Да/Нет",
};

interface FilledValue {
  id: number;
  attribute_id: number;
  attribute_slug: string;
  attribute_name: string;
  input_type: "dropdown" | "multiselect" | "text" | "numeric" | "boolean";
  unit: string | null;
  value_id: number | null;
  value_label: string | null;
  value_text: string | null;
  value_numeric: number | null;
  value_boolean: boolean | null;
}

interface AvailableAttr {
  attribute_id: number;
  attribute_slug: string;
  attribute_name: string;
  input_type: FilledValue["input_type"];
  unit: string | null;
  available_values: { id: number; slug: string; label: string }[];
}

interface ProductAttrs {
  product_id: number;
  product_type_id: number | null;
  product_type_name: string | null;
  filled: FilledValue[];
  available: AvailableAttr[];
}

// Editable draft (по attribute_id)
type DraftValue = {
  attribute_id: number;
  input_type: FilledValue["input_type"];
  attribute_name: string;
  unit: string | null;
  available_values?: { id: number; label: string }[];
  value_id: number | null;
  value_text: string;
  value_numeric: string; // input string → parseFloat at submit
  value_boolean: boolean;
};

function filledToDraft(f: FilledValue): DraftValue {
  return {
    attribute_id: f.attribute_id,
    input_type: f.input_type,
    attribute_name: f.attribute_name,
    unit: f.unit,
    value_id: f.value_id,
    value_text: f.value_text ?? "",
    value_numeric: f.value_numeric != null ? String(f.value_numeric) : "",
    value_boolean: f.value_boolean ?? false,
  };
}

function availableToDraft(a: AvailableAttr): DraftValue {
  return {
    attribute_id: a.attribute_id,
    input_type: a.input_type,
    attribute_name: a.attribute_name,
    unit: a.unit,
    available_values: a.available_values,
    value_id: null,
    value_text: "",
    value_numeric: "",
    value_boolean: false,
  };
}

function draftToPayload(d: DraftValue): Record<string, unknown> | null {
  const base = { attribute_id: d.attribute_id };
  switch (d.input_type) {
    case "dropdown":
    case "multiselect":
      if (d.value_id == null) return null;
      return { ...base, value_id: d.value_id };
    case "text":
      if (!d.value_text.trim()) return null;
      return { ...base, value_text: d.value_text.trim() };
    case "numeric":
      if (!d.value_numeric.trim()) return null;
      return { ...base, value_numeric: parseFloat(d.value_numeric) };
    case "boolean":
      return { ...base, value_boolean: d.value_boolean };
  }
}

export function AttributesTab({ productId }: { productId: number }): JSX.Element {
  const queryClient = useQueryClient();

  const { data: attrs, isLoading } = useQuery({
    queryKey: ["product-attrs", productId],
    queryFn: async () => {
      const { data } = await api.get(`/products/${productId}/attributes`);
      return data as ProductAttrs;
    },
  });

  const { data: types } = useQuery({
    queryKey: ["product-types"],
    queryFn: async () => {
      const { data } = await api.get("/product-types");
      return data as ProductType[];
    },
  });

  const [drafts, setDrafts] = useState<DraftValue[]>([]);

  useEffect(() => {
    if (!attrs) return;
    // Каждый раз пересобираем — filled и available из бекенда могли поменяться
    const filledDrafts = attrs.filled.map(filledToDraft);
    // available — те что в product_type, но ещё не заполнены
    const availableDrafts = attrs.available.map(availableToDraft);
    // Сохраняем available_values из available даже для filled (нужно для select)
    const enriched = filledDrafts.map((d) => {
      const av = attrs.available.find(
        (a) => a.attribute_id === d.attribute_id,
      );
      if (av) return { ...d, available_values: av.available_values };
      return d;
    });
    setDrafts([...enriched, ...availableDrafts]);
  }, [attrs]);

  const setTypeMutation = useMutation({
    mutationFn: async (typeId: number | null) => {
      await api.patch(`/products/${productId}/product-type`, {
        product_type_id: typeId,
      });
    },
    onSuccess: () => {
      toast.success("Шаблон обновлён");
      queryClient.invalidateQueries({ queryKey: ["product-attrs", productId] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const values = drafts
        .map(draftToPayload)
        .filter((p): p is Record<string, unknown> => p !== null);
      await api.put(`/products/${productId}/attributes`, { values });
    },
    onSuccess: () => {
      toast.success("Характеристики сохранены");
      queryClient.invalidateQueries({ queryKey: ["product-attrs", productId] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  const [creatingAttr, setCreatingAttr] = useState(false);
  const [newAttr, setNewAttr] = useState({
    slug: "",
    name: "",
    input_type: "dropdown" as FilledValue["input_type"],
    unit: "",
  });

  const currentType = types?.find((t) => t.id === attrs?.product_type_id);

  const createAttrMutation = useMutation({
    mutationFn: async () => {
      const { data: created } = await api.post("/attributes", {
        slug: newAttr.slug.trim(),
        name: newAttr.name.trim(),
        input_type: newAttr.input_type,
        unit: newAttr.unit.trim() || null,
      });
      // Привязываем к текущему шаблону — иначе атрибут создастся, но не
      // появится в списке характеристик этого товара.
      if (currentType) {
        await api.patch(`/product-types/${currentType.id}`, {
          slug: currentType.slug,
          name: currentType.name,
          description: currentType.description,
          has_variants: currentType.has_variants,
          attribute_ids: [...currentType.attribute_ids, created.id],
        });
      }
      return created;
    },
    onSuccess: () => {
      toast.success("Атрибут создан и привязан к шаблону");
      setCreatingAttr(false);
      setNewAttr({ slug: "", name: "", input_type: "dropdown", unit: "" });
      queryClient.invalidateQueries({ queryKey: ["product-types"] });
      queryClient.invalidateQueries({ queryKey: ["attributes"] });
      queryClient.invalidateQueries({ queryKey: ["product-attrs", productId] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка создания атрибута"),
  });

  const addValueMutation = useMutation({
    mutationFn: async ({
      attributeId,
      slug,
      label,
    }: {
      attributeId: number;
      slug: string;
      label: string;
    }) => {
      await api.post(`/attributes/${attributeId}/values`, {
        slug: slug.trim(),
        label: label.trim(),
        sort_order: 0,
      });
    },
    onSuccess: () => {
      toast.success("Значение добавлено");
      queryClient.invalidateQueries({ queryKey: ["attributes"] });
      queryClient.invalidateQueries({ queryKey: ["product-attrs", productId] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка добавления значения"),
  });

  if (isLoading) return <Skeleton className="h-96" />;
  if (!attrs) return <div className="text-sm text-muted-foreground">Нет данных</div>;

  const updateDraft = (attrId: number, patch: Partial<DraftValue>) => {
    setDrafts((prev) =>
      prev.map((d) => (d.attribute_id === attrId ? { ...d, ...patch } : d)),
    );
  };

  const removeDraft = (attrId: number) => {
    setDrafts((prev) => prev.filter((d) => d.attribute_id !== attrId));
  };

  return (
    <div className="space-y-4 pt-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
            Шаблон (Product Type)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Select
              value={attrs.product_type_id ? String(attrs.product_type_id) : "none"}
              onValueChange={(v) =>
                setTypeMutation.mutate(v === "none" ? null : Number(v))
              }
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Не выбран" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Без шаблона</SelectItem>
                {types?.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link
              to="/configuration/product-types"
              className="text-xs text-primary underline"
            >
              Управлять шаблонами →
            </Link>
          </div>
          {!attrs.product_type_id && (
            <p className="text-xs text-muted-foreground mt-2">
              Выберите шаблон чтобы увидеть список характеристик,
              привязанных к нему.
            </p>
          )}
        </CardContent>
      </Card>

      {attrs.product_type_id && (
        <Card>
          <CardContent className="pt-4">
            {creatingAttr ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Slug *</Label>
                    <Input
                      value={newAttr.slug}
                      onChange={(e) =>
                        setNewAttr({ ...newAttr, slug: e.target.value })
                      }
                      placeholder="warranty"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Название *</Label>
                    <Input
                      value={newAttr.name}
                      onChange={(e) =>
                        setNewAttr({ ...newAttr, name: e.target.value })
                      }
                      placeholder="Гарантия"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Тип ввода</Label>
                    <Select
                      value={newAttr.input_type}
                      onValueChange={(v: any) =>
                        setNewAttr({ ...newAttr, input_type: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(NEW_ATTR_INPUT_TYPE_LABELS).map(
                          ([k, l]) => (
                            <SelectItem key={k} value={k}>
                              {l}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Единица (опционально)</Label>
                    <Input
                      value={newAttr.unit}
                      onChange={(e) =>
                        setNewAttr({ ...newAttr, unit: e.target.value })
                      }
                      placeholder="см, кг, мес"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setCreatingAttr(false)}>
                    Отмена
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => createAttrMutation.mutate()}
                    disabled={
                      !newAttr.slug.trim() ||
                      !newAttr.name.trim() ||
                      createAttrMutation.isPending
                    }
                  >
                    Создать и привязать
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setCreatingAttr(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                Новый атрибут
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {drafts.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Атрибутов не привязано к шаблону или шаблон не выбран.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
              Характеристики ({drafts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {drafts.map((d) => (
              <div
                key={d.attribute_id}
                className="grid grid-cols-[200px_1fr_auto] items-center gap-3 py-2 border-b last:border-0"
              >
                <Label className="flex items-center gap-2">
                  {d.attribute_name}
                  {d.unit && (
                    <Badge variant="secondary" className="text-[10px]">
                      {d.unit}
                    </Badge>
                  )}
                </Label>

                <div>
                  {(d.input_type === "dropdown" ||
                    d.input_type === "multiselect") && (
                    <div className="flex items-center gap-2">
                      <Select
                        value={d.value_id ? String(d.value_id) : ""}
                        onValueChange={(v) =>
                          updateDraft(d.attribute_id, { value_id: Number(v) })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="— выбрать —" />
                        </SelectTrigger>
                        <SelectContent>
                          {(d.available_values ?? []).map((v) => (
                            <SelectItem key={v.id} value={String(v.id)}>
                              {v.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <InlineAddValue
                        pending={addValueMutation.isPending}
                        onAdd={(slug, label) =>
                          addValueMutation.mutate({
                            attributeId: d.attribute_id,
                            slug,
                            label,
                          })
                        }
                      />
                    </div>
                  )}
                  {d.input_type === "text" && (
                    <Input
                      value={d.value_text}
                      onChange={(e) =>
                        updateDraft(d.attribute_id, {
                          value_text: e.target.value,
                        })
                      }
                    />
                  )}
                  {d.input_type === "numeric" && (
                    <Input
                      type="number"
                      step="0.01"
                      value={d.value_numeric}
                      onChange={(e) =>
                        updateDraft(d.attribute_id, {
                          value_numeric: e.target.value,
                        })
                      }
                    />
                  )}
                  {d.input_type === "boolean" && (
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={d.value_boolean}
                        onChange={(e) =>
                          updateDraft(d.attribute_id, {
                            value_boolean: (e.target as HTMLInputElement)
                              .checked,
                          })
                        }
                      />
                      Да
                    </label>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeDraft(d.attribute_id)}
                  title="Очистить"
                >
                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                </Button>
              </div>
            ))}

            <div className="flex justify-end pt-3 border-t">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                <Plus className="w-4 h-4 mr-1" />
                Сохранить характеристики
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function InlineAddValue({
  pending,
  onAdd,
}: {
  pending: boolean;
  onAdd: (slug: string, label: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");

  if (!open) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0"
        title="Добавить значение"
        onClick={() => setOpen(true)}
      >
        <Plus className="w-3.5 h-3.5" />
      </Button>
    );
  }

  function submit() {
    onAdd(slug, label);
    setSlug("");
    setLabel("");
    setOpen(false);
  }

  return (
    <div className="flex items-center gap-1 shrink-0">
      <Input
        className="h-9 w-24 text-xs"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        placeholder="slug"
        autoFocus
      />
      <Input
        className="h-9 w-28 text-xs"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Название"
      />
      <Button
        type="button"
        size="sm"
        onClick={submit}
        disabled={!slug.trim() || !label.trim() || pending}
      >
        +
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
        ×
      </Button>
    </div>
  );
}
